import React, { useState, useEffect, useCallback } from 'react';
import { Shield, X, Loader2 } from 'lucide-react';
import type { Role } from '../../services/security/roles.service';
import { RolesService } from '../../services/security/roles.service';
import { RolePermissionsEditor } from './RolePermissionsEditor';
import { ConfirmationToast } from '../ui/ConfirmationToast';
import { NotificationToast } from '../ui/NotificationToast';
import { usePermissions } from '../../hooks/usePermissions';

interface RoleDetailsDrawerProps {
  isOpen: boolean;
  role: Role | null;
  onClose: () => void;
  onEditRole: (role: Role) => void;
  onToggleRoleStatus: (role: Role) => Promise<void>;
}

export const RoleDetailsDrawer: React.FC<RoleDetailsDrawerProps> = ({
  isOpen,
  role,
  onClose,
  onEditRole,
  onToggleRoleStatus,
}) => {
  const { hasPermission } = usePermissions();
  const canAdministerRoles = hasPermission('Roles', 'administrar');

  const [isVisible, setIsVisible] = useState(false);
  const [currentRole, setCurrentRole] = useState<Role | null>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'modules'>('modules');

  // Estados para confirmación y loading
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState<'toggle' | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingRole, setIsLoadingRole] = useState(false);

  // Estados para notificaciones
  const [showNotification, setShowNotification] = useState(false);
  const [notificationConfig, setNotificationConfig] = useState<{
    title: string;
    message: string;
    type: 'success' | 'error' | 'warning' | 'info';
  } | null>(null);

  // Animación de apertura/cierre
  useEffect(() => {
    if (isOpen && role) {
      setCurrentRole(role);
      const timer = setTimeout(() => setIsVisible(true), 10);
      return () => clearTimeout(timer);
    } else if (!isOpen) {
      setIsVisible(false);
    }
  }, [isOpen, role]);

  // Resetear estados al cerrar
  useEffect(() => {
    if (!isVisible && !isOpen) {
      const timer = setTimeout(() => {
        setCurrentRole(null);
        setActiveTab('modules');
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isVisible, isOpen]);

  // Cargar detalle completo del rol al abrir (información general)
  const fetchRoleDetails = useCallback(async (roleId: string) => {
    setIsLoadingRole(true);
    try {
      const detailedRole = await RolesService.getRoleById(roleId);
      setCurrentRole(detailedRole);
    } catch (error) {
      console.error('Error cargando detalle del rol:', error);
      setNotificationConfig({
        title: 'Error',
        message: 'No se pudieron cargar los detalles completos del rol',
        type: 'error',
      });
      setShowNotification(true);
    } finally {
      setIsLoadingRole(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen && role) {
      fetchRoleDetails(role.id);
    }
  }, [isOpen, role, fetchRoleDetails]);

  const handleClose = () => {
    setIsVisible(false);
    onClose();
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'No disponible';
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (activo: boolean) => {
    return activo
      ? 'bg-green-100 text-green-800 border-green-200'
      : 'bg-red-100 text-red-800 border-red-200';
  };

  const handleEditRole = () => {
    if (currentRole) onEditRole(currentRole);
  };

  const handleToggleRoleStatus = () => {
    setConfirmationAction('toggle');
    setShowConfirmation(true);
  };

  const handleConfirmAction = async () => {
    if (!currentRole || !confirmationAction) return;

    setIsLoading(true);
    try {
      if (confirmationAction === 'toggle') {
        await onToggleRoleStatus(currentRole);

        setNotificationConfig({
          title: '¡Éxito!',
          message: `Rol ${currentRole.activo ? 'desactivado' : 'activado'} correctamente`,
          type: 'success',
        });
        setShowNotification(true);

        await fetchRoleDetails(currentRole.id);
      }
    } catch (error) {
      console.error('Error al ejecutar acción:', error);
      setNotificationConfig({
        title: 'Error',
        message: 'No se pudo cambiar el estado del rol. Inténtalo nuevamente.',
        type: 'error',
      });
      setShowNotification(true);
    } finally {
      setIsLoading(false);
      setShowConfirmation(false);
      setConfirmationAction(null);
    }
  };

  const handleCancelAction = () => {
    setShowConfirmation(false);
    setConfirmationAction(null);
  };

  const getConfirmationConfig = () => {
    if (!currentRole || !confirmationAction) return null;

    if (confirmationAction === 'toggle') {
      return {
        title: currentRole.activo ? 'Desactivar Rol' : 'Activar Rol',
        message: currentRole.activo
          ? `¿Estás seguro de que deseas desactivar el rol "${currentRole.nombre}"? Los usuarios con este rol perderán los permisos asociados.`
          : `¿Estás seguro de que deseas activar el rol "${currentRole.nombre}"? Los usuarios con este rol recuperarán los permisos asociados.`,
        confirmText: currentRole.activo ? 'Desactivar' : 'Activar',
        type: currentRole.activo ? ('danger' as const) : ('success' as const),
      };
    }

    return null;
  };

  if (!isOpen && !currentRole) return null;
  if (!currentRole) return null;

  return (
    <div className="fixed left-0 right-0 bottom-0 top-0 z-[60] overflow-hidden">
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute inset-0 cursor-pointer"
          onClick={handleClose}
          aria-label="Cerrar drawer"
        />

        <section
          className={`absolute right-0 top-0 h-full w-full max-w-3xl border-l border-gray-200 transform transition-transform duration-300 ease-in-out ${
            isVisible ? 'translate-x-0' : 'translate-x-full'
          }`}
        >
          <div className="h-full flex flex-col bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-xs font-medium text-gray-900">Detalles del Rol</h2>
              <button
                onClick={handleClose}
                className="text-gray-500 hover:text-gray-700 transition-colors p-1 rounded-lg hover:bg-gray-100"
                aria-label="Cerrar"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              {/* Header del rol */}
              <div className="text-center py-6 bg-gray-50/50">
                <div className="mx-auto h-16 w-16 rounded-full bg-gradient-to-r from-purple-400 to-blue-500 flex items-center justify-center mb-3 shadow-md">
                  <Shield className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xs font-medium text-gray-900 mb-1">{currentRole.nombre}</h3>
                {currentRole.descripcion && (
                  <p className="text-gray-600 text-xs mb-2">{currentRole.descripcion}</p>
                )}
                <div className="flex justify-center">
                  <span
                    className={`inline-flex px-3 py-1 text-xs font-medium rounded-full border ${getStatusColor(
                      currentRole.activo,
                    )}`}
                  >
                    {currentRole.activo ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
              </div>

              {/* Tabs */}
              <div className="px-6 border-b border-gray-200">
                <div className="flex space-x-8">
                  <button
                    onClick={() => setActiveTab('info')}
                    className={`py-3 text-xs font-medium border-b-2 transition-colors ${
                      activeTab === 'info'
                        ? 'text-purple-600 border-purple-600'
                        : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Información General
                  </button>
                  <button
                    onClick={() => setActiveTab('modules')}
                    className={`py-3 text-xs font-medium border-b-2 transition-colors ${
                      activeTab === 'modules'
                        ? 'text-purple-600 border-purple-600'
                        : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    Módulos y Permisos
                  </button>
                </div>
              </div>

              {/* Contenido de las pestañas */}
              <div className="px-6 py-6">
                {activeTab === 'info' && (
                  <div>
                    {/* Información básica */}
                    <div className="mb-6">
                      <h4 className="text-xs font-medium text-gray-900 mb-4 border-b border-gray-200 pb-2">
                        Información Básica
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-medium text-gray-500">
                            Nombre del Rol
                          </label>
                          <p className="text-gray-900 font-medium">{currentRole.nombre}</p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Estado</label>
                          <div className="flex items-center space-x-2">
                            <div
                              className={`w-3 h-3 rounded-full ${
                                currentRole.activo ? 'bg-green-500' : 'bg-red-500'
                              }`}
                            />
                            <p className="text-gray-900 font-medium">
                              {currentRole.activo ? 'Activo' : 'Inactivo'}
                            </p>
                          </div>
                        </div>
                        {currentRole.descripcion && (
                          <div className="md:col-span-2">
                            <label className="block text-xs font-medium text-gray-500">
                              Descripción
                            </label>
                            <p className="text-gray-900">{currentRole.descripcion}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Información del Sistema */}
                    <div className="pt-4 border-t border-gray-200">
                      <h4 className="text-xs font-medium text-gray-600 mb-3">
                        Información del Sistema
                      </h4>
                      <div className="space-y-2 text-xs text-gray-500 bg-gray-50 p-3 rounded-lg">
                        <div>
                          <span className="font-medium">ID:</span> {currentRole.id}
                        </div>
                        <div>
                          <span className="font-medium">Creado:</span>{' '}
                          {formatDate(currentRole.creadoEn)}
                        </div>
                        <div>
                          <span className="font-medium">Última actualización:</span>{' '}
                          {formatDate(currentRole.actualizadoEn)}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'modules' && (
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="px-5 py-3 bg-gray-50/80 border-b border-gray-100 flex items-center justify-between">
                      <h4 className="text-xs font-medium text-gray-900">Módulos y Permisos</h4>
                      {isLoadingRole && <Loader2 className="w-4 h-4 animate-spin text-gray-400" />}
                    </div>
                    <div className="p-5">
                      <RolePermissionsEditor
                        roleId={currentRole.id}
                        readOnly={!canAdministerRoles}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer contextual: solo acciones del rol en la pestaña de información */}
            {activeTab === 'info' && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <div className="flex space-x-3">
                  <button
                    onClick={handleEditRole}
                    className="flex-1 bg-purple-600 text-white px-3 py-2 rounded-lg hover:bg-purple-700 transition-colors font-medium text-xs"
                  >
                    Editar Rol
                  </button>
                  <button
                    onClick={handleToggleRoleStatus}
                    disabled={isLoading}
                    className={`flex-1 px-3 py-2 rounded-lg border transition-colors font-medium text-xs disabled:opacity-50 disabled:cursor-not-allowed ${
                      currentRole.activo
                        ? 'border-red-200 bg-red-50 text-red-700 hover:bg-red-100'
                        : 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100'
                    }`}
                  >
                    {currentRole.activo ? 'Desactivar' : 'Activar'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>

      {/* Confirmation Toast */}
      {(() => {
        const config = getConfirmationConfig();
        return config ? (
          <ConfirmationToast
            isOpen={showConfirmation}
            title={config.title}
            message={config.message}
            confirmText={config.confirmText}
            type={config.type}
            onConfirm={handleConfirmAction}
            onCancel={handleCancelAction}
            isLoading={isLoading}
          />
        ) : null;
      })()}

      {/* Notification Toast */}
      {notificationConfig && (
        <NotificationToast
          isOpen={showNotification}
          title={notificationConfig.title}
          message={notificationConfig.message}
          type={notificationConfig.type}
          onClose={() => {
            setShowNotification(false);
            setNotificationConfig(null);
          }}
          duration={7000}
        />
      )}
    </div>
  );
};

export default RoleDetailsDrawer;
